'use client';

import { AgeRestriction, ContentDetailTabType } from '@diritto/common/content/constants';
import { EpisodeOrderType } from '@diritto/common/episode/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { ReplyOrder } from '@diritto/common/reply/constants';
import { TrackerExtraInfo, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { ContentDetailEpisodeList } from 'app-components/contents/detail/ContentDetailEpisodeList';
import { ContentDetailInfo } from 'app-components/contents/detail/ContentDetailInfo';
import { ContentDetailNoticeList } from 'app-components/contents/detail/ContentDetailNoticeList';
import { ContentDetailTab } from 'app-components/contents/detail/ContentDetailTab';
import { FancyShareList } from 'app-components/fancyShare/FancyShareList';
import { ContentReplyListView } from 'app-components/reply/list/ContentReplyList';
import { RollingAddButton } from 'app-components/rolling/RollingAddButton';
import { RollingList } from 'app-components/rolling/RollingList';
import { DesktopContentWidth, DesktopGlobalMenuWidth, isPC } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { useAuthState } from 'contexts/AuthProvider';
import { EpisodeReplyProvider } from 'contexts/EpisodeReplyProvider';
import { FancyShareProvider } from 'contexts/FancyShareProvider';
import { RollingProvider } from 'contexts/RollingProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useSearchParams } from 'next/navigation';
import { useGetContentFancySharesInfiniteQuery } from 'queries/fancyShare/userGetContentFancySharesInfiniteQuery';
import { useGetContentRollingsInfiniteQuery } from 'queries/relay/useGetContentRollingsInfiniteQuery';
import React, { useEffect } from 'react';
import { useContentDetailEpisodeOrderType } from 'state-hooks/contents/useContentDetailEpisodeOrderType';
import { useReplyOrderType } from 'state-hooks/reply/useReplyOrderType';
import {
  ContentBodyContainer,
  ContentBodyNoPaddingContainer,
} from 'ui-components/layout/containers/ContentBodyCenterContainer';

export const ContentDetailView = ({
  content,
  authorProfile,
  trackerExtraInfo,
}: {
  content: ContentPublicRelayDto;
  authorProfile?: ProfileLitePublicRelayDto;
  trackerExtraInfo: TrackerExtraInfo;
}) => {
  const searchParams = useSearchParams();
  const { includeAdult, profile } = useAuthState();
  const initEpisodeOrderType = searchParams?.get('episodeOrderType') as EpisodeOrderType | undefined;
  const intiReplyOrderType = searchParams?.get('replyOrderType') as ReplyOrder | undefined;
  const tabType = (searchParams?.get('tabType') ?? ContentDetailTabType.Episode) as ContentDetailTabType;

  const { orderType: episodeOrderType } = useContentDetailEpisodeOrderType({
    initialOrderType: initEpisodeOrderType,
  });
  const { orderType: replyOrderType } = useReplyOrderType({
    initialOrderType: intiReplyOrderType,
  });
  const contentFancySharesQuery = useGetContentFancySharesInfiniteQuery(content.id, includeAdult);
  const contentRollingQuery = useGetContentRollingsInfiniteQuery(content.id, includeAdult);

  const { tracker } = useTrackingState();

  useEffect(() => {
    tracker.track(TrackingEventType.ViewContent, { contentId: content.id });
  }, [content.id, tracker]);

  return (
    <>
      <ContentBodyContainer>
        <StyledContentArea>
          <ContentDetailInfo content={content} profile={authorProfile} />
        </StyledContentArea>
        <ContentDetailNoticeList content={content} />
        <ContentDetailTab
          content={content}
          tabType={tabType}
          episodeOrderType={episodeOrderType}
          replyOrderType={replyOrderType}
        />
      </ContentBodyContainer>
      <ContentBodyNoPaddingContainer>
        {tabType === ContentDetailTabType.Episode && (
          <ContentDetailEpisodeList
            contentId={content.id}
            isAdultContent={content.ageRestriction === AgeRestriction.Adult}
            episodeOrderType={episodeOrderType}
          />
        )}
        {tabType === ContentDetailTabType.Reply && (
          <EpisodeReplyProvider
            contentId={content.id}
            authorProfile={authorProfile}
            orderBy={intiReplyOrderType}
            isInputFocus={false}>
            <ContentReplyListView />
          </EpisodeReplyProvider>
        )}
        {tabType === ContentDetailTabType.FancyShare && (
          <FancyShareProvider fancySharesQuery={contentFancySharesQuery}>
            <FancyShareList trackerExtraInfo={trackerExtraInfo} />
          </FancyShareProvider>
        )}
        {tabType === ContentDetailTabType.Rolling && (
          <RollingProvider rollingsQuery={contentRollingQuery}>
            <RollingList trackerExtraInfo={trackerExtraInfo} />
            {content.profileId === profile?.id && (
              <StyledBottomWindowWrapper>
                <StyledContentContainer>
                  <StyledRollingAddButtonWrapper>
                    <RollingAddButton contentId={content.id} />
                  </StyledRollingAddButtonWrapper>
                </StyledContentContainer>
              </StyledBottomWindowWrapper>
            )}
          </RollingProvider>
        )}
      </ContentBodyNoPaddingContainer>
    </>
  );
};

const StyledContentArea = styled.div`
  padding-top: 30px;
`;

const StyledBottomWindowWrapper = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;

  display: flex;
  justify-content: center;

  pointer-events: none;

  z-index: ${ZIndex.BottomWindowWrapper};
`;

const StyledContentContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: right;

  pointer-events: none;

  @media (${isPC}) {
    width: ${DesktopContentWidth + DesktopGlobalMenuWidth}px;
  }
`;

const StyledRollingAddButtonWrapper = styled.div`
  pointer-events: auto;
  padding: 0 16px 16px 0;
`;
